/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstanceLive } from 'instance/Axios';
import { handleCatchError } from 'utils/CatchHandler';
import {
    fieldNameToLabel,
    isVolumeField,
    formatIndianCurrency,
    formatIndianCount,
    isAllMetricsEmpty,
    categorizeError,
    buildAriaLabel
} from 'views/urban-dashboard/utils/formatters';

/**
 * API endpoint map by role.
 */
const API_ENDPOINTS = {
    distributor: 'api/V2/moneytransfer/distributor/Urban/GetCountsDashboard',
    'super-distributor': 'api/V2/moneytransfer/superDistributor/Urban/GetCountsDashboard'
};

/**
 * Transforms the API `count` object into an array of MetricItem objects.
 * Each metric item includes fieldName, label, value (coalesced to 0), isVolume,
 * formattedValue, and ariaLabel.
 * @param {object} count - The count object from the API response
 * @returns {Array<{fieldName: string, label: string, value: number, isVolume: boolean, formattedValue: string, ariaLabel: string}>}
 */
function transformMetrics(count) {
    if (!count || typeof count !== 'object') return [];

    return Object.entries(count).map(([fieldName, rawValue]) => {
        const value = rawValue ?? 0;
        const label = fieldNameToLabel(fieldName);
        const isVolume = isVolumeField(fieldName);
        const formattedValue = isVolume ? formatIndianCurrency(value) : formatIndianCount(value);
        const ariaLabel = buildAriaLabel(label, formattedValue);

        return {
            fieldName,
            label,
            value,
            isVolume,
            formattedValue,
            ariaLabel
        };
    });
}

/**
 * Custom hook that fetches Urban Dashboard metrics for the given role.
 *
 * State machine: 'loading' → 'loaded' | 'error' | 'empty'
 *
 * @param {'distributor' | 'super-distributor'} role
 * @returns {{ state: string, metrics: Array|null, error: {category: string, message: string}|null, retry: () => void }}
 */
export function useUrbanDashboardData(role) {
    const [state, setState] = useState('loading');
    const [metrics, setMetrics] = useState(null);
    const [error, setError] = useState(null);

    // Use a ref to track whether the component is still mounted
    const mountedRef = useRef(true);

    const fetchData = useCallback(async () => {
        setState('loading');
        setMetrics(null);
        setError(null);

        // Check for auth token before making API call
        const token = localStorage.getItem('token');
        if (!token) {
            const sessionError = categorizeError({ isSessionError: true, message: 'Session unavailable' });
            setState('error');
            setError(sessionError);
            return;
        }

        const endpoint = API_ENDPOINTS[role];
        if (!endpoint) {
            setState('error');
            setError({ category: 'server', message: 'Invalid role specified.' });
            return;
        }

        try {
            const response = await axiosInstanceLive.post(
                endpoint,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 30000
                }
            );

            // Guard against state updates after unmount
            if (!mountedRef.current) return;

            const count = response?.data?.count;

            if (isAllMetricsEmpty(count)) {
                setState('empty');
                setMetrics(null);
            } else {
                const transformedMetrics = transformMetrics(count);
                setState('loaded');
                setMetrics(transformedMetrics);
            }
        } catch (err) {
            // Guard against state updates after unmount
            if (!mountedRef.current) return;

            handleCatchError(err);
            const errorInfo = categorizeError(err);
            setState('error');
            setError(errorInfo);
        }
    }, [role]);

    // Call API once per mount (or when role changes)
    useEffect(() => {
        mountedRef.current = true;
        fetchData();

        return () => {
            mountedRef.current = false;
        };
    }, [fetchData]);

    // Retry function resets to loading and re-invokes the API
    const retry = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return { state, metrics, error, retry };
}

export default useUrbanDashboardData;
